import React, { useEffect, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { getRecommendations } from '../../api/cabinetApi';
import { Recommendations } from '../../types/cabinet.types';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface RecommendationModalProps {
  visitId: string | number;
  isOpen: boolean;
  onClose: () => void;
}

export const RecommendationModal: React.FC<RecommendationModalProps> = ({
  visitId,
  isOpen,
  onClose,
}) => {
  const { t } = useI18n();
  const [data, setData] = useState<Recommendations | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const isDietOrder = typeof visitId === 'string' && visitId.toString().startsWith('order_');

  useEffect(() => {
    if (!isOpen || !visitId) return;
    setIsLoading(true);
    setError('');
    getRecommendations(visitId)
      .then(setData)
      .catch(() => setError(t('err_fetch_recommendations') || 'Nie udało się pobrać zaleceń'))
      .finally(() => setIsLoading(false));
  }, [isOpen, visitId]);

  if (!isOpen) return null;

  // Отримуємо Bearer token для побудови URL print сторінки
  // Токен зберігається в sessionStorage (див. client.ts interceptor)
  const token = sessionStorage.getItem('cab_token') || '';

  const handleOpenPrint = () => {
    if (!data?.printUrl) return;
    const url = data.printUrl + (data.printUrl.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(token);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Форматуємо текст залечень: розбиваємо на параграфи
  const renderText = (text: string) => {
    const paragraphs = text.split(/\n{2,}/).filter(p => p.trim());
    return paragraphs.map((para, i) => {
      const lines = para.trim();
      // Заголовки — рядки з великих літер або що починаються з ключових слів
      const isHeading = /^[A-ZŁŻŹĆĄĘÓŚŃ\s]{5,}$/.test(lines.toUpperCase().slice(0, 60)) && lines.length < 80;
      if (isHeading) {
        return <h3 key={i} className="cab-rec-heading">{lines}</h3>;
      }
      const htmlLines = lines.split('\n').map((line, j) => (
        <React.Fragment key={j}>
          {line}
          {j < lines.split('\n').length - 1 && <br />}
        </React.Fragment>
      ));
      return <p key={i} className="cab-rec-para">{htmlLines}</p>;
    });
  };

  return (
    <div className="cab-rec-overlay" onClick={onClose}>
      <div className="cab-rec-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="cab-rec-header">
          <div>
            <h2 className="cab-rec-title">
              {isDietOrder
                ? (t('dietitian_recommendations') || 'Zalecenia dietetyka')
                : (t('doctor_recommendations') || 'Zalecenia lekarza')}
            </h2>
            {data?.date && (
              <p className="cab-rec-date">{t('visit_no') || 'Wizyta'} #{visitId} · {data.date}</p>
            )}
          </div>
          <button className="cab-rec-close" onClick={onClose} aria-label={t('close') || 'Zamknij'}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="cab-rec-body">
          {isLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <LoadingSpinner size={28} />
            </div>
          )}
          {error && (
            <p className="cab-rec-error">{error}</p>
          )}
          {!isLoading && !error && data && (
            <>
              {data.text ? (
                <div className="cab-rec-content">
                  {isDietOrder || /<\/?[a-z][\s\S]*>/i.test(data.text) ? (
                    <div 
                      dangerouslySetInnerHTML={{ __html: data.text }} 
                      className="cab-rec-html-container"
                    />
                  ) : (
                    renderText(data.text)
                  )}
                </div>
              ) : (
                <p className="cab-rec-empty">{t('no_recommendations') || 'Brak zaleceń dla tej wizyty.'}</p>
              )}
            </>
          )}
        </div>

        {/* Footer: кнопка PDF/Print */}
        {!isDietOrder && data && data.text && (
          <div className="cab-rec-footer">
            <button className="cab-rec-print-btn" onClick={handleOpenPrint}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 6 2 18 2 18 9"/>
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                <rect x="6" y="14" width="12" height="8"/>
              </svg>
              {t('print_save_pdf') || 'Drukuj / Zapisz PDF'}
            </button>
          </div>
        )}
      </div>

      <style>{`
        .cab-rec-overlay {
          position: fixed;
          inset: 0;
          background: rgba(30, 10, 60, 0.5);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: flex-end;
          justify-content: center;
          z-index: 9999;
          padding: 0;
          animation: cab-rec-fade-in 0.2s ease;
        }
        @keyframes cab-rec-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .cab-rec-modal {
          background: #fff;
          border-radius: 24px 24px 0 0;
          width: 100%;
          max-width: 680px;
          max-height: 90dvh;
          display: flex;
          flex-direction: column;
          animation: cab-rec-slide-up 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
          box-shadow: 0 -10px 60px rgba(54, 23, 119, 0.15);
        }
        @keyframes cab-rec-slide-up {
          from { transform: translateY(60px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @media (min-width: 600px) {
          .cab-rec-overlay {
            align-items: center;
            padding: 20px;
          }
          .cab-rec-modal {
            border-radius: 24px;
            max-height: 85vh;
          }
        }
        .cab-rec-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 20px 20px 16px;
          border-bottom: 1px solid #F0EDF6;
          flex-shrink: 0;
        }
        .cab-rec-title {
          font-family: 'Poppins', sans-serif;
          font-size: 18px;
          font-weight: 700;
          color: var(--cab-violet, #361777);
          margin: 0 0 4px;
        }
        .cab-rec-date {
          font-family: 'Poppins', sans-serif;
          font-size: 13px;
          color: var(--cab-note, #786798);
          margin: 0;
        }
        .cab-rec-close {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: none;
          background: #F4F0FA;
          color: var(--cab-violet, #361777);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          transition: background 0.2s;
        }
        .cab-rec-close:hover { background: #E6D6FA; }
        .cab-rec-body {
          flex: 1;
          overflow-y: auto;
          padding: 20px;
          overscroll-behavior: contain;
        }
        .cab-rec-content {
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          line-height: 1.7;
          color: #222;
        }
        .cab-rec-html-container * {
          color: inherit !important;
          background-color: transparent !important;
        }
        .cab-rec-html-container strong, .cab-rec-html-container b {
          font-weight: bold !important;
        }
        .cab-rec-html-container h1, .cab-rec-html-container h2, .cab-rec-html-container h3 {
          margin-top: 1em;
          margin-bottom: 0.5em;
          font-weight: 700;
        }
        .cab-rec-html-container ul, .cab-rec-html-container ol {
          padding-left: 20px;
          margin-bottom: 1em;
        }
        .cab-rec-html-container p {
          margin-bottom: 1em;
        }
        .cab-rec-heading {
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--cab-violet, #361777);
          margin: 20px 0 8px;
          padding-bottom: 6px;
          border-bottom: 1px solid #E6D6FA;
        }
        .cab-rec-heading:first-child {
          margin-top: 0;
        }
        .cab-rec-para {
          margin: 0 0 12px;
          color: #333;
        }
        .cab-rec-para:last-child {
          margin-bottom: 0;
        }
        .cab-rec-empty {
          text-align: center;
          color: var(--cab-note, #786798);
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          padding: 40px 0;
        }
        .cab-rec-error {
          text-align: center;
          color: #E53E3E;
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          padding: 40px 0;
        }
        .cab-rec-footer {
          padding: 16px 20px 24px;
          border-top: 1px solid #F0EDF6;
          flex-shrink: 0;
        }
        .cab-rec-print-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          height: 48px;
          border-radius: 100px;
          border: 2px solid var(--cab-violet, #361777);
          background: transparent;
          color: var(--cab-violet, #361777);
          font-family: 'Poppins', sans-serif;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s, color 0.2s;
        }
        .cab-rec-print-btn:hover {
          background: var(--cab-violet, #361777);
          color: #fff;
        }
      `}</style>
    </div>
  );
};
